import { ShieldCheck, Lock, FileCheck, UserCheck } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Reveal } from "@/components/common/Reveal";
import { CTASection } from "@/components/common/CTASection";
import { StatusBadge } from "@/components/common/StatusBadge";
import { PRIVACY_POINTS } from "@/data/content";

const toneIcon = { positive: ShieldCheck, neutral: FileCheck, warning: Lock };
const toneAccent = {
  positive: "text-ff-teal bg-ff-teal/12",
  neutral: "text-ff-ivory bg-white/8",
  warning: "text-ff-amber bg-ff-amber/12",
};

export default function PrivacyTrust() {
  return (
    <div data-testid="privacy-trust-page">
      <PageHeader
        overline="Privacy & Trust"
        titleLines={["Safeguards you", "can rely on."]}
        lead="Trust is the product. Here's exactly how FanFuel protects your accounts, your content, your files and your money decisions."
        testid="privacy-header"
      >
        <div className="flex flex-wrap gap-2.5">
          <StatusBadge tone="positive">No passwords</StatusBadge>
          <StatusBadge tone="positive">You stay owner</StatusBadge>
          <StatusBadge tone="ember">Human approval</StatusBadge>
        </div>
      </PageHeader>

      <section className="px-5 py-14 md:px-8 md:py-20">
        <div className="mx-auto grid max-w-7xl gap-5 md:grid-cols-2 lg:grid-cols-3">
          {PRIVACY_POINTS.map((p, i) => {
            const Icon = toneIcon[p.tone] || UserCheck;
            return (
              <Reveal key={p.title} delay={(i % 3) * 0.08}>
                <div className="h-full rounded-2xl border border-white/8 bg-ff-slate/60 p-7 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-white/20" data-testid={`privacy-point-${i}`}>
                  <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${toneAccent[p.tone]}`}>
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-6 font-display text-lg font-semibold text-ff-ivory">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ff-grey">{p.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* approval gate visual */}
      <section className="px-5 pb-6 md:px-8">
        <Reveal className="mx-auto flex max-w-4xl flex-col items-start gap-6 rounded-3xl border border-white/10 bg-ff-slate/70 p-8 backdrop-blur-xl md:flex-row md:items-center md:p-12">
          <span className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl bg-ff-ember/15 text-ff-ember">
            <UserCheck className="h-8 w-8" />
          </span>
          <div>
            <h3 className="font-display text-2xl font-semibold text-ff-ivory md:text-3xl">
              Sensitive actions require approval.
            </h3>
            <p className="mt-3 text-base leading-relaxed text-ff-grey">
              Money movement and other high-impact actions are gated behind explicit human review. AI can
              prepare and flag — it never has the final say on your money.
            </p>
          </div>
        </Reveal>
      </section>

      <CTASection
        title="Trust the process. Keep control."
        secondaryLabel="How cashouts work"
        secondaryTo="/cashouts"
      />
    </div>
  );
}
